import type { CaseParticipantView, InvitationIssuedResponse } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { errorMessage } from '../lib/api'
import { formatDate, humanize } from '../lib/format'
import { useInviteParticipant, useResendInvitation, useRevokeParticipant } from '../lib/participants'
import { Alert, Button, Card, Field, Input, Mono, Textarea } from './ui'

const STATUS_STYLE: Record<CaseParticipantView['status'], string> = {
  invited: 'bg-amber-100 text-amber-800',
  verified: 'bg-emerald-100 text-emerald-800',
  revoked: 'bg-neutral-200 text-neutral-500',
  expired: 'bg-neutral-200 text-neutral-500',
}

function ParticipantStatus({ status }: { status: CaseParticipantView['status'] }) {
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status]}`}>{humanize(status)}</span>
}

/**
 * Who can open the borrower side of a case. The share link is shown exactly once, right after
 * it is issued; afterwards only re-sending (which rotates it) produces a new one.
 */
export function ParticipantsCard({
  caseId,
  participants,
  defaultEmail,
  defaultName,
  canInvite,
  canRevoke,
}: {
  caseId: string
  participants: CaseParticipantView[]
  defaultEmail: string
  defaultName: string
  canInvite: boolean
  canRevoke: boolean
}) {
  const [issued, setIssued] = useState<InvitationIssuedResponse | null>(null)
  const active = participants.find((p) => p.status === 'invited' || p.status === 'verified')
  const history = participants.filter((p) => p !== active)

  return (
    <Card title="Borrower access">
      {issued && <IssuedLink issued={issued} onDismiss={() => setIssued(null)} />}

      {active ? (
        <ParticipantRow
          caseId={caseId}
          participant={active}
          canResend={canInvite && active.status === 'invited'}
          canRevoke={canRevoke}
          onIssued={setIssued}
        />
      ) : canInvite ? (
        <InviteForm caseId={caseId} defaultEmail={defaultEmail} defaultName={defaultName} onIssued={setIssued} />
      ) : (
        <p className="text-sm text-neutral-500">No one has been invited to complete the application yet.</p>
      )}

      {history.length > 0 && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-neutral-500">Previous invitations ({history.length})</summary>
          <ul className="mt-2 space-y-1">
            {history.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 text-neutral-500">
                <ParticipantStatus status={p.status} />
                <Mono>{p.email}</Mono>
                <span className="text-xs text-neutral-400">
                  invited {formatDate(p.invited_at)}
                  {p.revoked_at && <> · revoked {formatDate(p.revoked_at)}</>}
                  {p.revoked_reason && <> — {humanize(p.revoked_reason)}</>}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  )
}

export function IssuedLink({ issued, onDismiss }: { issued: InvitationIssuedResponse; onDismiss?: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.link)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }
  return (
    <div className="mb-4 space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
      <p>
        Invitation sent to <strong>{issued.participant.email}</strong>. This link is shown once — copy it now if you
        want to hand it over another way. It expires {formatDate(issued.participant.invite_expires_at)} and only works
        when the buyer signs in as that address.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input readOnly value={issued.link} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        <Button type="button" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        {onDismiss && (
          <Button type="button" variant="secondary" onClick={onDismiss}>
            Done
          </Button>
        )}
      </div>
    </div>
  )
}

function InviteForm({
  caseId,
  defaultEmail,
  defaultName,
  onIssued,
}: {
  caseId: string
  defaultEmail: string
  defaultName: string
  onIssued: (issued: InvitationIssuedResponse) => void
}) {
  const [key] = useState(() => crypto.randomUUID())
  const [email, setEmail] = useState(defaultEmail)
  const [name, setName] = useState(defaultName)
  const invite = useInviteParticipant(caseId, key)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    invite.mutate({ email: email.trim().toLowerCase(), display_name: name.trim() || null }, { onSuccess: onIssued })
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-sm text-neutral-500">
        Send the buyer a private link to complete their application. What they enter there is theirs and Tex's — it
        is never shown to the dealership.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Buyer email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={invite.isPending} />
        </Field>
        <Field label="Buyer name">
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={invite.isPending} />
        </Field>
      </div>
      {invite.isError && <Alert tone="error">{errorMessage(invite.error)}</Alert>}
      <div className="flex justify-end">
        <Button type="submit" disabled={invite.isPending}>
          {invite.isPending ? 'Sending…' : 'Send invitation'}
        </Button>
      </div>
    </form>
  )
}

function ParticipantRow({
  caseId,
  participant,
  canResend,
  canRevoke,
  onIssued,
}: {
  caseId: string
  participant: CaseParticipantView
  canResend: boolean
  canRevoke: boolean
  onIssued: (issued: InvitationIssuedResponse) => void
}) {
  const [resendKey, setResendKey] = useState(() => crypto.randomUUID())
  const resend = useResendInvitation(caseId, resendKey)
  const revoke = useRevokeParticipant(caseId)
  const [revoking, setRevoking] = useState(false)
  const [reason, setReason] = useState('')
  const expired = participant.invite_expires_at !== null && new Date(participant.invite_expires_at).getTime() < Date.now()

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <ParticipantStatus status={participant.status} />
        <span className="font-medium">{participant.display_name ?? participant.email}</span>
        <Mono className="text-neutral-500">{participant.email}</Mono>
        <span className="text-xs text-neutral-400">· {humanize(participant.role)}</span>
      </div>
      <p className="text-xs text-neutral-500">
        Invited {formatDate(participant.invited_at)}
        {participant.status === 'invited' && participant.invite_expires_at && (
          <>
            {' '}
            · {expired ? 'link expired' : 'link expires'} {formatDate(participant.invite_expires_at)}
          </>
        )}
        {participant.verified_at && <> · signed in {formatDate(participant.verified_at)}</>}
      </p>

      {(canResend || canRevoke) && !revoking && (
        <div className="flex flex-wrap gap-2">
          {canResend && (
            <Button
              type="button"
              variant="secondary"
              disabled={resend.isPending}
              onClick={() =>
                resend.mutate(participant.id, {
                  onSuccess: (issued) => {
                    onIssued(issued)
                    setResendKey(crypto.randomUUID())
                  },
                })
              }
            >
              {resend.isPending ? 'Sending…' : expired ? 'Send a new link' : 'Re-send link'}
            </Button>
          )}
          {canRevoke && (
            <Button type="button" variant="danger" onClick={() => setRevoking(true)}>
              Revoke access
            </Button>
          )}
        </div>
      )}
      {resend.isError && <Alert tone="error">{errorMessage(resend.error)}</Alert>}

      {revoking && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            revoke.mutate({ participantId: participant.id, reason: reason.trim() }, { onSuccess: () => setRevoking(false) })
          }}
          className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3"
        >
          <Field label="Why is access being revoked?" hint="The buyer loses access immediately; nothing they entered is deleted.">
            <Textarea rows={2} required minLength={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={revoke.isPending} />
          </Field>
          {revoke.isError && <Alert tone="error">{errorMessage(revoke.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setRevoking(false)} disabled={revoke.isPending}>
              Keep access
            </Button>
            <Button type="submit" variant="danger" disabled={revoke.isPending || reason.trim().length < 2}>
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
